import React, { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtUsd, fmtSigned, fixed, shortHash, NETWORKS, ASSETS } from "@/lib/journal";
import { useTranslation } from "react-i18next";
import { CaretDown, Trash, Sparkle, MagnifyingGlass, CaretLeft, CaretRight } from "@phosphor-icons/react";

const netShort = (id) => NETWORKS.find((n) => n.id === id)?.short || id?.toUpperCase();
const PAGE_SIZE = 25;

// Orange Edit button = this trade still needs a Why Buy / Why Sell reason.
const needsReason = (t) => !t.why_buy || (t.status === "closed" && !t.why_sell);

export const TradesTable = ({
  trades, prices, statusFilter, setStatusFilter, assetFilter, setAssetFilter,
  assetOptions, onSell, onDelete, portfolioMap, showPortfolio, onSetMark, onEditNotes,
}) => {
  const { t: t_ } = useTranslation();
  const [expanded, setExpanded] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return trades;
    return trades.filter((t) =>
      [t.asset, t.reason, t.closing_notes, t.entry_tx_hash, t.exit_tx_hash,
       portfolioMap?.[t.portfolio_id], ...(t.tags || [])]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [trades, query, portfolioMap]);

  const pageCount = Math.max(1, Math.ceil(searched.length / PAGE_SIZE));

  useEffect(() => { setPage(1); }, [query, statusFilter, assetFilter, trades.length]);

  const pageRows = searched.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="border border-[#1F1F1F] bg-surface" data-testid="trades-table-card">
      <div className="flex flex-col gap-4 border-b border-[#1F1F1F] p-6 lg:flex-row lg:items-center lg:justify-between">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
          Positions <span className="ml-2 text-[#C58361]">{searched.length}</span>
        </h3>
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
            <Input
              data-testid="ledger-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search asset, tag, note, tx…"
              className="h-9 w-[220px] rounded-none border-[#1F1F1F] bg-[#050505] pl-8 text-xs focus-visible:ring-[#C58361]"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="h-9 w-[130px] rounded-none border-[#1F1F1F] bg-[#050505] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
              <SelectItem value="all" data-testid="filter-status-all">All Status</SelectItem>
              <SelectItem value="open" data-testid="filter-status-open">Open</SelectItem>
              <SelectItem value="closed" data-testid="filter-status-closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={assetFilter} onValueChange={setAssetFilter}>
            <SelectTrigger data-testid="filter-asset" className="h-9 w-[130px] rounded-none border-[#1F1F1F] bg-[#050505] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
              <SelectItem value="all" data-testid="filter-asset-all">All Assets</SelectItem>
              {[...new Set([...assetOptions, ...ASSETS])].map((a) => (
                <SelectItem key={a} value={a} data-testid={`filter-asset-${a}`}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {searched.length === 0 ? (
        <div className="m-6 border border-dashed border-[#1F1F1F] px-6 py-16 text-center" data-testid="trades-empty-state">
          <Sparkle size={20} className="mx-auto text-[#C58361]" weight="light" />
          <p className="mt-4 text-sm text-zinc-500">
            {query ? `Nothing matches “${query}”.` : "No trades found. Initiate your first entry."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-[#1F1F1F] text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-6 py-3 text-left font-medium">Asset</th>
                {showPortfolio && <th className="px-4 py-3 text-left font-medium">Portfolio</th>}
                <th className="px-4 py-3 text-left font-medium">Net</th>
                <th className="px-4 py-3 text-right font-medium">Volume</th>
                <th className="px-4 py-3 text-right font-medium">Entry</th>
                <th className="px-4 py-3 text-right font-medium">Cost Basis</th>
                <th className="px-4 py-3 text-right font-medium">Exit / Mark</th>
                <th className="px-4 py-3 text-right font-medium">PnL</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-6 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((t, i) => {
                const mark = t.status === "closed" ? t.exit_price
                  : t.manual_price ?? (t.priceable === false ? null : prices?.[t.asset?.toUpperCase()]);
                const pnl = t.status === "closed"
                  ? t.realized_pnl
                  : mark != null ? t.volume * (mark - t.entry_price) : null;
                const pct = pnl != null && t.cost_basis ? (pnl / t.cost_basis) * 100 : null;
                const isOpen = expanded === t.id;
                return (
                  <React.Fragment key={t.id}>
                    <tr
                      data-testid={`trade-row-${t.id}`}
                      onClick={() => setExpanded(isOpen ? null : t.id)}
                      className="cursor-pointer border-b border-[#1F1F1F] transition-colors duration-200 hover:bg-[#111111] fade-up"
                      style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <CaretDown size={12} className={`text-zinc-600 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                          <span className="font-mono text-white">{t.asset}</span>
                        </div>
                        {t.entry_tx_hash && (
                          <span className="ml-6 font-mono text-[10px] text-zinc-600">{shortHash(t.entry_tx_hash)}</span>
                        )}
                        {t.merged_from > 1 && (
                          <span
                            data-testid={`merged-badge-${t.id}`}
                            className="ml-2 border border-[#C58361]/40 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-[#C58361]"
                          >
                            merged {t.merged_from}
                          </span>
                        )}
                      </td>
                      {showPortfolio && (
                        <td className="px-4 py-4 text-xs text-zinc-400">
                          {portfolioMap?.[t.portfolio_id] || "—"}
                        </td>
                      )}
                      <td className="px-4 py-4">
                        <Badge className="rounded-none border border-[#1F1F1F] bg-transparent px-2 py-0.5 font-mono text-[10px] text-zinc-400 hover:bg-transparent">
                          {netShort(t.network)}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-right font-mono text-zinc-300">{t.volume}</td>
                      <td className="px-4 py-4 text-right font-mono text-zinc-300">{fmtUsd(t.entry_price, 4)}</td>
                      <td className="px-4 py-4 text-right font-mono text-zinc-300">{fmtUsd(t.cost_basis)}</td>
                      <td className="px-4 py-4 text-right font-mono text-zinc-300">
                        {mark != null ? fmtUsd(mark, 4) : (
                          <span data-testid={`price-unavailable-${t.id}`} className="text-[10px] uppercase tracking-wider text-zinc-500">
                            {t.status === "open" ? t_("trades.price_unavailable", "price unavailable") : "—"}
                          </span>
                        )}
                        {t.status === "open" && t.manual_price != null && (
                          <span className="ml-2 text-[9px] uppercase tracking-wider text-[#C58361]">manual</span>
                        )}
                      </td>
                      <td className={`px-4 py-4 text-right font-mono ${pnl == null ? "text-zinc-600" : pnl >= 0 ? "text-[#4ADE80]" : "text-[#EF4444]"}`}>
                        {pnl == null ? "—" : (
                          <>
                            {fmtSigned(pnl)}
                            <span className="ml-2 text-[11px] opacity-70">{Number.isFinite(pct) && pct >= 0 ? "+" : ""}{fixed(pct, 1, "0.0")}%</span>
                          </>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`border px-2 py-1 text-[10px] uppercase tracking-wider ${
                          t.status === "open"
                            ? "border-zinc-700 text-zinc-300"
                            : "border-[#C58361]/40 bg-[#C58361]/10 text-[#C58361]"
                        }`}>
                          {t.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            data-testid={`edit-btn-${t.id}`}
                            title={needsReason(t) ? "Reason not specified yet" : "Edit trade"}
                            onClick={(e) => { e.stopPropagation(); onEditNotes(t); }}
                            className={`h-8 rounded-none border px-3 text-xs transition-colors ${
                              needsReason(t)
                                ? "border-[#C58361] bg-[#C58361]/10 text-[#C58361] hover:bg-[#C58361]/20"
                                : "border-[#1F1F1F] bg-transparent text-zinc-300 hover:bg-[#111111] hover:text-white"
                            }`}
                          >
                            Edit
                          </Button>
                          {t.status === "open" && (
                            <Button
                              data-testid={`mark-btn-${t.id}`}
                              onClick={(e) => { e.stopPropagation(); onSetMark(t); }}
                              className="h-8 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-zinc-300 hover:bg-[#111111] hover:text-white"
                            >
                              Mark
                            </Button>
                          )}
                          {t.status === "open" && (
                            <Button
                              data-testid={`sell-btn-${t.id}`}
                              onClick={(e) => { e.stopPropagation(); onSell(t); }}
                              className="h-8 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-white hover:bg-[#C58361] hover:text-black"
                            >
                              Sell
                            </Button>
                          )}
                          <Button
                            data-testid={`delete-btn-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); onDelete(t.id); }}
                            className="h-8 w-8 rounded-none border border-[#1F1F1F] bg-transparent p-0 text-zinc-500 hover:bg-[#EF4444]/10 hover:text-[#EF4444]"
                          >
                            <Trash size={13} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-[#1F1F1F] bg-[#080808]" data-testid={`trade-detail-${t.id}`}>
                        <td colSpan={showPortfolio ? 10 : 9} className="px-6 py-6">
                          <div className="grid gap-6 md:grid-cols-2">
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Reason for Purchase</p>
                              <p className="mt-2 text-sm text-zinc-300">{t.reason || "—"}</p>
                              {t.merged_from > 1 && (
                                <p className="mt-2 font-mono text-[11px] text-[#C58361]" data-testid={`merged-hint-${t.id}`}>
                                  Merged from {t.merged_from} buys at a blended average cost
                                  {t.merged_tx_hashes?.length ? ` · ${t.merged_tx_hashes.length} entry tx${t.merged_tx_hashes.length === 1 ? "" : "s"}` : ""}
                                </p>
                              )}
                              {t.tags?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {t.tags.map((tag) => (
                                    <span key={tag} className="border border-[#C58361]/40 px-2 py-0.5 text-[10px] text-[#C58361]">{tag}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Closing Thoughts</p>
                              <p className="mt-2 text-sm text-zinc-300">{t.closing_notes || "—"}</p>
                              {t.exit_tx_hash && (
                                <p className="mt-3 font-mono text-[11px] text-zinc-600">Exit tx: {shortHash(t.exit_tx_hash)}</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {searched.length > 0 && pageCount > 1 && (
        <div className="flex items-center justify-between border-t border-[#1F1F1F] px-6 py-4" data-testid="ledger-pagination">
          <span className="font-mono text-[11px] text-zinc-600">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, searched.length)} of {searched.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              data-testid="ledger-prev-page"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="h-8 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-zinc-300 hover:bg-[#111111] disabled:opacity-30"
            >
              <CaretLeft size={12} />
            </Button>
            <span className="font-mono text-xs text-zinc-400" data-testid="ledger-page-indicator">
              {page} / {pageCount}
            </span>
            <Button
              data-testid="ledger-next-page"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page === pageCount}
              className="h-8 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-zinc-300 hover:bg-[#111111] disabled:opacity-30"
            >
              <CaretRight size={12} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
